import React from 'react';
import {
  pickLocalized,
  countAnsweredInSection,
  formatCountdown
} from '../../utils/testUtils';
import {
  testBtnPrimaryLg,
  testLink,
  testSectionCard,
  testFocusRing,
  testTimerOk,
  testTimerUrgent,
} from './testUiClasses';

function AbilitySectionMenu({
  t,
  lang,
  paperTitle,
  paperInstruction,
  sections,
  answers,
  remainingSeconds,
  allQuestionsAnswered,
  onOpenSection,
  onBackToPaperGrid,
  onSubmitEntireTest
}) {
  const sectionCount = sections.length;
  const gridCols =
    sectionCount <= 2
      ? 'grid-cols-1 sm:grid-cols-2'
      : sectionCount <= 4
        ? 'grid-cols-2'
        : 'grid-cols-2 lg:grid-cols-3';

  return (
    <div className="min-h-[calc(100vh-8rem)] flex flex-col -mx-4 md:-mx-8 px-4 md:px-8 py-4 md:py-6">
      <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6 shrink-0">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold text-ink font-montserrat">
            {paperTitle}
          </h1>
          <p className="text-ink-muted mt-2 text-sm md:text-base text-justify max-w-2xl">
            {t('test.sectionMenuSubtitle', { chapter: paperTitle })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <div
            className={`text-sm font-mono font-semibold px-3 py-1.5 rounded-md ${
              remainingSeconds <= 300 ? testTimerUrgent : testTimerOk
            }`}
          >
            {t('test.timeRemaining')}: {formatCountdown(remainingSeconds)}
          </div>
          <button type="button" onClick={onBackToPaperGrid} className={testLink}>
            {t('test.backToChapters')}
          </button>
        </div>
      </header>

      {paperInstruction ? (
        <p className="text-sm text-ink-muted mb-6 text-justify max-w-3xl shrink-0">{paperInstruction}</p>
      ) : null}

      <div className={`grid ${gridCols} gap-3 md:gap-4 flex-1 auto-rows-fr min-h-0`}>
        {sections.map((section, index) => {
          const total = (section.questions || []).length;
          const answered = countAnsweredInSection(section, answers);
          const complete = total > 0 && answered >= total;
          const title = pickLocalized(section.title, lang);

          return (
            <button
              key={section.id}
              type="button"
              onClick={() => onOpenSection(section.id)}
              className={`${testSectionCard} ${testFocusRing}`}
            >
              <div>
                <span className="text-xs font-semibold uppercase tracking-wider text-link">
                  {t('test.sectionLabel', { number: index + 1 })}
                </span>
                <h2 className="text-lg md:text-xl font-bold mt-1 leading-snug text-ink">{title}</h2>
              </div>
              <div className="mt-4 flex items-end justify-between gap-2">
                <span className="text-sm text-link">
                  {t('test.sectionProgress', { answered, total })}
                </span>
                {complete ? (
                  <span className="text-xs font-semibold bg-primary text-primary-foreground px-2 py-1 rounded-md">
                    {t('test.sectionComplete')}
                  </span>
                ) : answered > 0 ? (
                  <span className="text-xs font-semibold bg-accent/30 text-ink border border-edge px-2 py-1 rounded-md">
                    {t('test.sectionInProgress')}
                  </span>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>

      {allQuestionsAnswered ? (
        <div className="mt-6 pt-6 border-t border-edge shrink-0 flex justify-center">
          <button
            type="button"
            onClick={onSubmitEntireTest}
            className={testBtnPrimaryLg}
          >
            {t('test.submitTest')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default AbilitySectionMenu;
